/**
 * @fileoverview List Row Actions Component
 * Dropdown menu with actions for each list row in the table
 */

import React, { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { MoreHorizontal, Eye, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { useDeleteList } from '../hooks/use-lists';
import { ListResponse } from '@/types/lists.types';

interface ListRowActionsProps {
  list: ListResponse;
}

export const ListRowActions: React.FC<ListRowActionsProps> = ({ list }) => {
  const navigate = useNavigate();
  const deleteList = useDeleteList();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleView = () => {
    navigate({
      to: '/dashboard/lists/detail/$listId',
      params: { listId: list.id },
    });
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteList.mutateAsync(list.id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete list:', error);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleView}>
            <Eye className="mr-2 h-4 w-4" />
            View List
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleDeleteClick}
            disabled={deleteList.isPending}
            className="text-brand focus:text-brand"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete List
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete List</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{list.listName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteList.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteList.isPending}
              className="bg-brand text-primary-foreground hover:bg-brand/90"
            >
              {deleteList.isPending ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
